import React, { useState } from 'react';
import { Table, Button, Modal, Spinner } from 'react-bootstrap';
import { DailyResponse, DailyResponseData } from '../../../../services/agent-report/response/DailyResponse';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import useDailyDetail from '../../../../services/agent-report/hooks/useDailyDetail';

interface AgentTableReportFilter {
    property_id: string;
    base_on: string;
    start_date: string;
    end_date: string;
    room_type_id?: string;
}

interface AgentTableReportProps {
    data: DailyResponse;
    filter: AgentTableReportFilter;
}

const AgentTableReport: React.FC<AgentTableReportProps> = ({ data, filter }) => {
    const [selectedAgent, setSelectedAgent] = useState<(DailyResponseData & { source: string }) | null>(null);

    const columnsReservations = [
        { label: "Agent Name", key: "name" },
        { label: "Percentage", key: "percentage" },
        { label: "No Reservations", key: "total_booking" },
        { label: "Nb Nights", key: "nights" },
        { label: "Avg. Length of Stay", key: "avg_stay" },
        { label: "Avg. Earning", key: "avg_earning" },
        { label: "Total Earning (IDR)", key: "total_earning" },
    ];

    const totalPercentage = parseFloat(
        data.data.reduce((acc, curr) => acc + curr.percentage, 0).toFixed(2)
    );

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Agent Report - Daily", 14, 10);

        const tableData = data.data.map(row => columnsReservations.map(col => {
            if (col.key === 'avg_earning' || col.key === 'total_earning') {
                return formatPrice(Number(row[col.key as keyof DailyResponseData]));
            } else if (col.key === 'percentage') {
                return `${row[col.key as keyof DailyResponseData]}%`;
            }
            return row[col.key as keyof DailyResponseData] ?? '';
        }));

        autoTable(doc, {
            head: [columnsReservations.map(col => col.label)],
            body: tableData,
        });

        doc.save("AgentReport_Daily.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columnsReservations.map(col => col.label),
            ...data.data.map(row => columnsReservations.map(col => {
                if (col.key === 'avg_earning' || col.key === 'total_earning') {
                    return formatPrice(Number(row[col.key as keyof DailyResponseData]));
                } else if (col.key === 'percentage') {
                    return `${row[col.key as keyof DailyResponseData]}%`;
                }
                return row[col.key as keyof DailyResponseData];
            })),
            ["Total", `${totalPercentage}%`, data.total.total_booking, data.total.total_nights, data.total.avg_stay, formatPrice(Number(data.total.avg_earning)), formatPrice(Number(data.total.total_earning))]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "AgentReport_Daily.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsReservations.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.data.length > 0 ? (
                            data.data.map((row: DailyResponseData, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columnsReservations.map((col, colIndex) => (
                                        <td key={colIndex}>{
                                            col.key === 'name' ? (
                                                row.source ? (
                                                    <Button variant="link" className="p-0" onClick={() => setSelectedAgent({ ...row, source: row.source! })}>
                                                        {row.name}
                                                    </Button>
                                                ) : row.name
                                            ) : col.key === 'avg_earning' || col.key === 'total_earning' ?
                                                formatPrice(Number(row[col.key as keyof DailyResponseData]))
                                                : col.key === 'percentage' ?
                                                    `${row[col.key as keyof DailyResponseData]}%`
                                                    : row[col.key as keyof DailyResponseData]
                                        }</td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columnsReservations.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><strong>{totalPercentage}%</strong></td>
                            <td><strong>{data.total.total_booking}</strong></td>
                            <td><strong>{data.total.total_nights}</strong></td>
                            <td><strong>{data.total.avg_stay}</strong></td>
                            <td><strong>{formatPrice(Number(data.total.avg_earning))}</strong></td>
                            <td><strong>{formatPrice(Number(data.total.total_earning))}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>

            {selectedAgent && (
                <AgentReservationDetailModal
                    agent={selectedAgent}
                    filter={filter}
                    onClose={() => setSelectedAgent(null)}
                />
            )}
        </div>
    );
};

export default AgentTableReport;

interface AgentReservationDetailModalProps {
    // `source` is required here — only reachable via the Agent Name click guard,
    // which only fires when row.source is present.
    agent: DailyResponseData & { source: string };
    filter: AgentTableReportFilter;
    onClose: () => void;
}

// Reconciliation drill-down: lists every reservation-night behind `agent`'s
// total_earning, so the sum of rows shown here always matches that figure
// (see AgentReportController::dailyReportDetail — same grouping expression).
const AgentReservationDetailModal: React.FC<AgentReservationDetailModalProps> = ({ agent, filter, onClose }) => {
    const { data, isFetching, error } = useDailyDetail({
        filter: { ...filter, source: agent.source },
        enabled: true,
    });

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Reservations — {agent.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {isFetching ? (
                    <div className="d-flex align-items-center justify-content-center p-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading reservations...</span>
                    </div>
                ) : error ? (
                    <div className="text-danger text-center p-4">Failed to load reservations.</div>
                ) : !data || data.data.length === 0 ? (
                    <div className="text-muted text-center p-4">No reservations found.</div>
                ) : (
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Invoice Number</th>
                                <th>Guest</th>
                                <th>Room</th>
                                <th className="text-end">Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.data.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.date ?? '-'}</td>
                                    <td>{row.invoice_number ?? '-'}</td>
                                    <td>{row.customer_name ?? '-'}</td>
                                    <td>{row.room_number ?? '-'}</td>
                                    <td className="text-end">{formatPrice(row.rate)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4}><strong>Total ({data.total.reservations} reservations, {data.total.nights} nights)</strong></td>
                                <td className="text-end"><strong>{formatPrice(Number(data.total.total_earning))}</strong></td>
                            </tr>
                        </tfoot>
                    </Table>
                )}
            </Modal.Body>
        </Modal>
    );
};
